import { default as axiosDefault, AxiosError } from 'axios';

import { languageTypes } from 'src/types';
import { storageHelper } from 'src/app/helpers';
import { axios } from 'src/app/utils';
import { clamp } from 'lodash';
import { t } from 'i18next';
import message from 'antd/es/message';

const defaultDuration = 2;

export const handleResponseErrors = (
  error: AxiosError,
  handleMessage: (textMessage: languageTypes.ErrorTextsKeysType) => void,
  resetReducerStatesToDefault: () => void,
  getBaseURLs: () => void,
): boolean => {
  if (error.config && error.config.blockErrorHandler) {
    return false;
  }

  switch (error.response?.status) {
    case 500:
      handleMessage('internalServerError');
      return true;
    case 404:
      handleMessage('internalServerError');
      return true;
    case 401:
      handleMessage('sessionExpired');
      axios.setToken(undefined);
      storageHelper.removeKeyPair('token');
      storageHelper.removeKeyPair('tokenc');
      resetReducerStatesToDefault();
      return true;
    case 400:
      handleMessage('badRequest');
      return true;
    case 403:
      handleMessage('authorizationError');
      return true;
    case 455:
      handleMessage('oyunIdMissing');
      return true;
    default: {
      if (error.message === 'Network Error') {
        getBaseURLs();
        handleMessage('connectionError');
        return true;
      }
      return false;
    }
  }
};

export const displayErrorMessage = (
  content: string,
  duration = defaultDuration,
  key: string | undefined = undefined,
) => {
  message.error({
    content,
    duration: clamp(duration, 0, 5),
    className: 'error-message',
    key,
  });
};

export const displayWarning = (content: string, duration = defaultDuration, key: string | undefined = undefined) => {
  message.warning({
    content,
    duration: clamp(duration, 0, 5),
    className: 'warning-message',
    key,
  });
};

export const displaySuccessStatusMessage = (
  content: string,
  duration = defaultDuration,
  key: string | undefined = undefined,
) => {
  message.success({
    content,
    duration: clamp(duration, 0, 5),
    className: 'success-message',
    key,
  });
};

export const displayLoadingMessage = (
  content: string,
  duration = defaultDuration,
  key: string | undefined = undefined,
) => {
  message.loading({
    content,
    duration: clamp(duration, 0, 5),
    className: 'error-message',
    key,
  });
};

export const setErrorBorderIfErrorExists = (isExists: boolean, defaultStyle: React.CSSProperties = {}) => {
  if (isExists) {
    return {
      ...defaultStyle,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'var(--warning-color)',
    } as React.CSSProperties;
  }

  return {
    ...defaultStyle,
  } as React.CSSProperties;
};

export function displayGenericError(error: unknown, key: string | undefined = undefined) {
  if (axiosDefault.isAxiosError(error)) {
    const errorData: {
      message: string;
      statusCode: number;
    } = error?.response?.data;

    if (errorData && errorData.message) {
      displayErrorMessage(errorData.message, undefined, key);
    } else {
      if (error instanceof Error) {
        displayErrorMessage(error.message, undefined, key);
      } else {
        displayErrorMessage(t('errors.unexpectedError'), undefined, key);
      }
    }
  } else if (error instanceof Error) {
    displayErrorMessage(error.message);
  }
}
